/*
 * Closing a sales task: the outcome (and talk time) a Done carries, and the
 * reason a Skip carries. Skip is terminal (status `skipped`), not "next".
 */

import { slugify_reason } from './gen_config';

export interface CodeLabel {
  code: string;
  label: string;
}

// Fallback when GET /form sends no outcomes (mirrors the DB enum).
export const OUTCOMES: CodeLabel[] = [
  { code: 'CONNECTED', label: 'Connected' },
  { code: 'NO_ANSWER', label: 'No answer' },
  { code: 'BOOKED', label: 'Booked' },
  { code: 'MESSAGE_SENT', label: 'Message sent' },
  { code: 'WHATSAPP_SENT', label: 'WhatsApp sent' }
];

export const OUTCOME_REQUIRED = 'OUTCOME_REQUIRED';

// A bare string is a label; its code is derived by `code_of`.
const clean_list = (raw: unknown, code_of: (label: string) => string): CodeLabel[] =>
  (Array.isArray(raw) ? raw : [])
    .map((r) => {
      if (typeof r === 'string') {
        const label = r.trim();
        const code = code_of(label);
        return code ? { code, label } : null;
      }
      if (!r || typeof r !== 'object') return null;
      const { code, label } = r as { code?: unknown; label?: unknown };
      if (typeof code !== 'string' || !code) return null;
      return { code, label: typeof label === 'string' && label ? label : code };
    })
    .filter((r): r is CodeLabel => !!r);

export const outcome_options = (raw: unknown): CodeLabel[] => {
  const list = clean_list(raw, (label) => label);
  return list.length ? list : OUTCOMES;
};

// Bare strings slug like the Generation tab's skip-reason codes.
export const skip_reason_options = (raw: unknown): CodeLabel[] => clean_list(raw, slugify_reason);

export const label_for = (list: CodeLabel[], code: string | null | undefined): string =>
  list.find((o) => o.code === code)?.label || code || '';

// Minutes typed in the override box -> whole seconds; null when blank/invalid.
export const override_seconds = (minutes: string | number | null | undefined): number | null => {
  if (minutes === '' || minutes === null || minutes === undefined) return null;
  const n = Number(minutes);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 60);
};

export interface DoneInput {
  outcome: string;
  derived_talk: number;
  override: string | number | null | undefined;
  require_outcome: boolean;
}

// '' when Done may proceed, else the reason it may not.
export const validate_done = ({
  outcome,
  derived_talk,
  override,
  require_outcome
}: DoneInput): string => {
  if (require_outcome && !outcome) return 'Choose an outcome first';
  if (derived_talk === 0 && override !== '' && override !== null && override !== undefined) {
    if (override_seconds(override) === null) return 'Talk time must be a number of minutes';
  }
  return '';
};

export interface CompletePayload {
  uuid: string;
  reply: Record<string, unknown>;
  outcome?: string;
  talk_time_seconds_override?: number;
}

// The server derives talk time from Twilio; the override is sent only when it
// derived nothing, which is the only case it honours one.
export const build_complete_payload = (
  uuid: string,
  reply: Record<string, unknown>,
  { outcome, derived_talk, override }: Pick<DoneInput, 'outcome' | 'derived_talk' | 'override'>
): CompletePayload => {
  const body: CompletePayload = { uuid, reply };
  if (outcome) body.outcome = outcome;
  if (!derived_talk) {
    const s = override_seconds(override);
    if (s !== null && s > 0) body.talk_time_seconds_override = s;
  }
  return body;
};

export interface SkipPayload {
  uuid: string;
  skip_reason: string;
  skip_note?: string;
}

export const build_skip_payload = (
  uuid: string,
  code: string,
  note?: string | null
): SkipPayload => {
  const body: SkipPayload = { uuid, skip_reason: code };
  const trimmed = (note || '').trim();
  if (trimmed) body.skip_note = trimmed.slice(0, 255);
  return body;
};

// "4m 12s", "45s", "1h 3m"; "0s" for none.
export const format_talk_time = (seconds: number | null | undefined): string => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return m ? `${h}h ${m}m` : `${h}h`;
  if (m) return s ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

export const is_outcome_required_error = (e: unknown): boolean =>
  (e as { response?: { status?: number; data?: { error_code?: string } } })?.response?.data
    ?.error_code === OUTCOME_REQUIRED;
